import { InfoBanner } from '@ui';
import { AuthContext } from '@lp/core/auth';
import { useContext } from 'react';
import { ORGAN_DECLINED_TEXTS } from '@features/components/CaseOverview/components';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import {
  CaseStatus,
  OrganApprovalStatus,
  OrganType,
  OutcomeType,
  RandomizationResultType,
  ServiceType,
  TrialAnswer,
  TrialQuestion,
} from '@/api/apis/case';
import { getActionsForStatus, getLateralityBlockReason } from '@/utils/helpers';
import { RandomizationResultBanner } from './RandomizationResultBanner';
import { CaseDetailsPermissions } from '@/permissions';

export const CaseBanners = () => {
  const { checkPermission } = useContext(AuthContext);
  const {
    organApproval,
    randomization,
    trialProgram,
    questionnaire,
    organType,
    laterality,
    status,
    outcomeData,
    serviceType,
  } = useBaseCaseDetails();
  const canSeeTrialWarning = checkPermission(CaseDetailsPermissions.CaseTrialWarningRead);
  const isOrganPending = organApproval?.status === OrganApprovalStatus.Pending;
  const isOrganApproved = organApproval?.status === OrganApprovalStatus.Approved;
  const isOrganDeclined = organApproval?.status === OrganApprovalStatus.Declined;
  const isDenovo = trialProgram?.organ === OrganType.Lungs;
  const isEnhance = trialProgram?.organ === OrganType.Heart;
  const isTrialProgram = isDenovo || isEnhance;
  const isRandomizedOCS = randomization?.result === RandomizationResultType.OCS;
  const isRandomizedSCS = randomization?.result === RandomizationResultType.SCS;
  const isRandomized = isRandomizedOCS || isRandomizedSCS;
  const enhanceTrialQuestion = questionnaire?.find((q) => q.question === TrialQuestion.TypeOfEnhanceTrial);
  const isEnhancePartA = isEnhance && enhanceTrialQuestion?.answer === TrialAnswer.PartA;

  const showDeclinedOrganBanner = isOrganDeclined && (!isTrialProgram || isEnhancePartA);
  const showOrganRequiredWarning = isOrganDeclined && isTrialProgram && !isEnhancePartA;
  const showDenovoBanner = isOrganApproved && isTrialProgram && !isRandomized && isDenovo;
  const organDeclineReason = organApproval?.declineReason ? ORGAN_DECLINED_TEXTS[organApproval.declineReason] : '';

  const caseWasDeclined = !!outcomeData && outcomeData.outcomeType !== OutcomeType.Transplant;
  const { nextStatus } = getActionsForStatus(status, caseWasDeclined);
  const isDirectAcquisitionCase =
    serviceType === ServiceType.DirectAcquisition && status === CaseStatus.ClinicalScreening;
  const effectiveNextStatus = isDirectAcquisitionCase ? CaseStatus.Closed : nextStatus;
  const showLateralityRequiredBanner = getLateralityBlockReason({
    organType,
    laterality,
    nextStatus: effectiveNextStatus,
  });

  return (
    <>
      {showLateralityRequiredBanner && (
        <InfoBanner title="Laterality required" iconType="warning">
          Set the organ laterality (Left or Right) in the Case Details to proceed with case status transition.
        </InfoBanner>
      )}
      {isOrganPending && (
        <InfoBanner title="Organ approval required" iconType="infoWarning">
          Pending organ approval by Transplant Center
        </InfoBanner>
      )}
      {isOrganApproved && !isRandomized && (
        <InfoBanner title="Organ approved" iconType="success" withClose>
          The Transplant Center approved the organ.
        </InfoBanner>
      )}
      {showDeclinedOrganBanner && (
        <InfoBanner title="Organ declined" iconType="cross" withClose>
          The Transplant Center declined the organ. {organDeclineReason}
        </InfoBanner>
      )}
      {showOrganRequiredWarning && (
        <InfoBanner title="Organ approval required" iconType="cross" withClose>
          The Transplant Center declined the organ. {organDeclineReason} The randomization cannot be proceeded.
        </InfoBanner>
      )}
      {showDenovoBanner && (
        <InfoBanner title="DENOVO Trial Program" iconType="denovoOrgan">
          The Transplant Center must complete the randomization. Wait until the randomization process is completed.
        </InfoBanner>
      )}
      {trialProgram && !isEnhancePartA && canSeeTrialWarning && (
        <InfoBanner
          title={`${trialProgram.name} Trial Program`}
          iconType={isDenovo ? 'denovoOrgan' : 'enhanceOrgan'}
          withClose
        >
          Please check availability of appropriate SCS with OPO / Donor Hospital.
        </InfoBanner>
      )}
      <RandomizationResultBanner />
    </>
  );
};
